/**
 * The filter under one column heading, in the row that sticks beneath the
 * headings.
 *
 * Each kind of column gets the control that suits what it holds: a text box
 * for text, a ticked list of the values actually present for a short list,
 * a min and a max for a number, a from and a to for a date. The three that
 * need more room than a cell open a panel; the button left behind says what
 * is set ("3 selected", "≥ 500", "Last 7 days") so the row reads as a summary
 * of every filter at once. The × beside a set filter clears that one alone.
 *
 * Filters are not saved with the layout. A layout is how somebody likes the
 * table; a filter is a question they were asking today.
 */
import { useEffect, useRef, useState } from 'react';
import Icon from '../../lib/icons.jsx';
import { formatDate, parseDate } from '../../lib/dates.js';
import Popover from './Popover.jsx';
import {
  DATE_PRESETS, presetRange, presetOf, enumKey, asNumber, isEmptyFilter
} from './filters.js';

const TYPE_DELAY = 200;

export default function ColumnFilter({ column, type, meta, dateFmt }) {
  const value = column.getFilterValue();
  const label = meta.label || column.id;
  const set = next => column.setFilterValue(isEmptyFilter(type, next) ? undefined : next);
  const active = !isEmptyFilter(type, value);

  let control;
  if (type === 'enum') control = <EnumFilter column={column} value={value} onChange={set} meta={meta} label={label} />;
  else if (type === 'number') control = <NumberFilter column={column} value={value} onChange={set} label={label} />;
  else if (type === 'date') control = <DateFilter value={value} onChange={set} label={label} dateFmt={dateFmt} field={column.id} />;
  else control = <TextFilter value={value} onChange={set} label={label} />;

  return (
    <div className={`dg-fcell${active ? ' on' : ''}`}>
      {control}
      {active ? (
        <button
          type="button"
          className="dg-fx"
          title={`Clear the ${label} filter`}
          aria-label={`Clear the ${label} filter`}
          onClick={() => set(undefined)}
        >
          <Icon name="x" />
        </button>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- text */

/**
 * Contains, ignoring case. Typed into straight away but applied 200ms after
 * the last key, so a thousand rows are not re-filtered on every letter.
 */
function TextFilter({ value, onChange, label }) {
  const [draft, setDraft] = useState(value || '');
  const change = useRef(onChange);
  change.current = onChange;

  /* Cleared from outside (the × or Clear filters): the box follows. */
  useEffect(() => { setDraft(value || ''); }, [value]);

  useEffect(() => {
    if (draft === (value || '')) return undefined;
    const t = setTimeout(() => change.current(draft), TYPE_DELAY);
    return () => clearTimeout(t);
  }, [draft, value]);

  return (
    <input
      className="dg-fin"
      value={draft}
      placeholder="Contains…"
      aria-label={`Filter ${label}`}
      onChange={e => setDraft(e.target.value)}
      onKeyDown={e => {
        if (e.key === 'Enter') change.current(draft);
        if (e.key === 'Escape' && draft) { e.stopPropagation(); setDraft(''); change.current(''); }
      }}
    />
  );
}

/* ------------------------------------------------ the button and panel */

function FilterButton({ label, summary, active, children, wide }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const close = useRef(() => setOpen(false)).current;

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={`dg-fbtn${active ? ' on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Filter ${label}: ${summary}`}
        title={summary}
        onClick={() => setOpen(o => !o)}
      >
        <span>{summary}</span>
        <Icon name="chev" />
      </button>
      <Popover
        anchor={btn}
        open={open}
        onClose={close}
        label={`Filter ${label}`}
        className={`dg-fpop${wide ? ' wide' : ''}`}
      >
        <div className="dg-fphead">
          <Icon name="filter" />{label}
        </div>
        {children}
      </Popover>
    </>
  );
}

/* ---------------------------------------------------------------- enum */

/** [value, label] pairs from `meta.options`, which may also be plain strings. */
const optionPairs = options => (options || []).map(o => (Array.isArray(o) ? [String(o[0]), String(o[1])] : [String(o), String(o)]));

/**
 * A ticked list of what is actually in the column, with how many rows each
 * would keep given every other filter - so ticking a value that would show
 * nothing is a decision rather than a surprise.
 *
 * Everything ticked is no filter at all, and is stored that way: a value
 * that turns up tomorrow should show, not be quietly excluded by a list
 * written before it existed.
 */
function EnumFilter({ column, value, onChange, meta, label }) {
  const pairs = optionPairs(meta.options);
  const nameOf = key => (key === '' ? (meta.blank || '(Blank)') : (pairs.find(p => p[0] === key) || [key, key])[1]);
  const picked = Array.isArray(value) ? value : null;

  const summary = !picked ? 'All'
    : !picked.length ? 'None'
      : picked.length === 1 ? nameOf(picked[0])
        : `${picked.length} selected`;

  return (
    <FilterButton label={label} summary={summary} active={!!picked}>
      <EnumPanel column={column} value={picked} onChange={onChange} pairs={pairs} nameOf={nameOf} />
    </FilterButton>
  );
}

function EnumPanel({ column, value, onChange, pairs, nameOf }) {
  const [q, setQ] = useState('');

  /* Counted against every other filter, so the numbers are what ticking
     each value would actually show. */
  const counts = new Map();
  column.getFacetedRowModel().rows.forEach(r => {
    const k = enumKey(r.getValue(column.id));
    counts.set(k, (counts.get(k) || 0) + 1);
  });

  /* The page's own order first (stages run New → Lost, not A → Z), then
     anything else present alphabetically, blank last. */
  const listed = pairs.map(p => p[0]);
  const keys = [...new Set([...listed, ...counts.keys(), ...(value || [])])];
  const rank = k => (k === '' ? 2 : listed.includes(k) ? 0 : 1);
  keys.sort((a, b) => rank(a) - rank(b)
    || (rank(a) === 0 ? listed.indexOf(a) - listed.indexOf(b) : nameOf(a).localeCompare(nameOf(b))));
  const options = keys
    .filter(k => k !== '' || counts.get('') || (value || []).includes(''))
    .map(k => ({ key: k, name: nameOf(k), count: counts.get(k) || 0 }));

  const all = options.map(o => o.key);
  const ticked = new Set(value || all);
  const want = q.trim().toLowerCase();
  const shown = want ? options.filter(o => o.name.toLowerCase().includes(want)) : options;

  const commit = next => {
    const every = all.every(k => next.has(k));
    onChange(every ? undefined : [...next]);
  };
  const toggle = key => {
    const next = new Set(ticked);
    if (next.has(key)) next.delete(key); else next.add(key);
    commit(next);
  };
  const tickShown = on => {
    const next = new Set(ticked);
    shown.forEach(o => (on ? next.add(o.key) : next.delete(o.key)));
    commit(next);
  };

  return (
    <>
      <div className="srch dg-fpsearch">
        <Icon name="search" />
        <input
          data-autofocus
          value={q}
          placeholder="Find a value…"
          aria-label="Find a value"
          onChange={e => setQ(e.target.value)}
        />
      </div>
      <div className="dg-fpacts">
        <button type="button" className="frange" onClick={() => tickShown(true)}>
          <Icon name="check" />Select all
        </button>
        <button type="button" className="frange clear" onClick={() => tickShown(false)}>
          <Icon name="x" />Clear
        </button>
      </div>
      <div className="dg-optlist" role="group" aria-label="Values">
        {shown.length ? shown.map(o => (
          <label key={o.key || '(blank)'} className={`dg-opt${o.count ? '' : ' none'}`}>
            <input
              type="checkbox"
              className="chk"
              checked={ticked.has(o.key)}
              onChange={() => toggle(o.key)}
            />
            <span className={o.key === '' ? 'dim' : ''}>{o.name}</span>
            <em>{o.count}</em>
          </label>
        )) : <p className="dg-fpnote">Nothing matches “{q}”.</p>}
      </div>
    </>
  );
}

/* -------------------------------------------------------------- number */

const fmtNumber = v => (asNumber(v) === null ? '' : asNumber(v).toLocaleString('en-US'));

function NumberFilter({ column, value, onChange, label }) {
  const v = value || {};
  const lo = asNumber(v.min);
  const hi = asNumber(v.max);
  const summary = lo !== null && hi !== null ? `${fmtNumber(lo)} – ${fmtNumber(hi)}`
    : lo !== null ? `≥ ${fmtNumber(lo)}`
      : hi !== null ? `≤ ${fmtNumber(hi)}`
        : 'Any';

  return (
    <FilterButton label={label} summary={summary} active={lo !== null || hi !== null}>
      <NumberPanel column={column} value={v} onChange={onChange} />
    </FilterButton>
  );
}

function NumberPanel({ column, value, onChange }) {
  /* The range actually present, as a hint in the empty boxes. */
  const nums = column.getFacetedRowModel().rows
    .map(r => asNumber(r.getValue(column.id)))
    .filter(n => n !== null);
  const least = nums.length ? Math.min(...nums) : null;
  const most = nums.length ? Math.max(...nums) : null;

  const put = (end, text) => onChange({ ...value, [end]: text });

  return (
    <div className="dg-fprow">
      <div className="fld">
        <label htmlFor={`dg-min-${column.id}`}>At least</label>
        <input
          id={`dg-min-${column.id}`}
          data-autofocus
          type="number"
          inputMode="decimal"
          value={value.min ?? ''}
          placeholder={least === null ? '' : fmtNumber(least)}
          onChange={e => put('min', e.target.value)}
        />
      </div>
      <div className="fld">
        <label htmlFor={`dg-max-${column.id}`}>At most</label>
        <input
          id={`dg-max-${column.id}`}
          type="number"
          inputMode="decimal"
          value={value.max ?? ''}
          placeholder={most === null ? '' : fmtNumber(most)}
          onChange={e => put('max', e.target.value)}
        />
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- date */

function DateFilter({ value, onChange, label, dateFmt, field }) {
  const v = value || {};
  const idOf = end => `dg-${end}-${String(field).replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  const preset = presetOf(v);
  const day = iso => formatDate(parseDate(iso), false, dateFmt);
  const summary = preset ? DATE_PRESETS.find(p => p[0] === preset)[1]
    : v.from && v.to ? (v.from === v.to ? day(v.from) : `${day(v.from)} – ${day(v.to)}`)
      : v.from ? `From ${day(v.from)}`
        : v.to ? `Until ${day(v.to)}`
          : 'Any time';

  return (
    <FilterButton label={label} summary={summary} active={!!(v.from || v.to)} wide>
      <div className="dg-fprow">
        <div className="fld">
          <label htmlFor={idOf('from')}>From</label>
          <input
            id={idOf('from')}
            data-autofocus
            type="date"
            value={v.from || ''}
            max={v.to || undefined}
            onChange={e => onChange({ ...v, from: e.target.value })}
          />
        </div>
        <div className="fld">
          <label htmlFor={idOf('to')}>To</label>
          <input
            id={idOf('to')}
            type="date"
            value={v.to || ''}
            min={v.from || undefined}
            onChange={e => onChange({ ...v, to: e.target.value })}
          />
        </div>
      </div>
      <div className="franges dg-fpacts">
        {DATE_PRESETS.map(([key, name]) => (
          <button
            key={key}
            type="button"
            className={`frange${preset === key ? ' on' : ''}`}
            onClick={() => onChange(presetRange(key))}
          >
            {name}
          </button>
        ))}
      </div>
    </FilterButton>
  );
}
